import { useMemo } from 'react';
import { QueryHookOptions } from '@apollo/client';

import {
  MembershipStoredValueExpirationScheduleQuery,
  StoredValueProgramFilter,
  StoredValueProgramsQuery,
  StoredValueProgramsQueryVariables,
  useMembershipStoredValueExpirationScheduleQuery,
  useStoredValueProgramsQuery as useApolloStoredValueProgramsQuery,
  useStoredValueProgramsQuery,
} from '@occc/graphql';
import { PaginationInfo, usePaginatedQuery } from '@occc-app/hooks/usePaginatedQuery';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';

const transformStoredValuePrograms = (data: StoredValueProgramsQuery) => {
  const connection = data.storedValuePrograms;
  return connection.edges.map((edge) => edge.node);
};

type GetStoredValueProgramsProps = {
  filter?: StoredValueProgramFilter;
  pageSize?: number;
  baseOptions?: QueryHookOptions<StoredValueProgramsQuery, StoredValueProgramsQueryVariables>;
  paginationInfo?: PaginationInfo;
  onPageChange?: (info: PaginationInfo) => void;
};

export const useGetStoredValueProgramsQuery = ({
  filter,
  pageSize,
  baseOptions,
  paginationInfo,
  onPageChange,
}: GetStoredValueProgramsProps = {}) => {
  const response = usePaginatedQuery(
    useApolloStoredValueProgramsQuery,
    'storedValuePrograms.pageInfo',
    pageSize,
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      variables: {
        filter,
      },
      ...baseOptions,
      onCompleted: (data) => {
        if (baseOptions?.onCompleted) {
          baseOptions.onCompleted(data);
        }
      },
    } as QueryHookOptions,
    paginationInfo,
    onPageChange,
  );

  return {
    ...response,
    data: response.data ? transformStoredValuePrograms(response.data) : response.data,
  };
};

type UseGetStoredValueProgramByIdProps = {
  id: string | undefined;
  chainId: string | undefined;
  baseOptions?: QueryHookOptions<StoredValueProgramsQuery, StoredValueProgramsQueryVariables>;
};

export const useGetStoredValueProgramById = ({
  id,
  chainId,
  baseOptions,
}: UseGetStoredValueProgramByIdProps) => {
  const response = useStoredValueProgramsQuery({
    variables: {
      filter: {
        ...(id ? { idEquals: id } : {}),
        ...(chainId ? { chainIdEquals: chainId } : {}),
      },
      first: 1,
    },
    skip: !id || !chainId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    ...baseOptions,
  });

  const program = response.data?.storedValuePrograms.edges[0]?.node;

  return {
    ...response,
    data: program,
  };
};

const STORED_VALUE_EXPIRATION_PAGINATION_PREFIX = 'stored-value-expiration-schedule';
const STORED_VALUE_EXPIRATION_PAGE_SIZE = 10;

type UseStoredValueExpirationScheduleParams = {
  memberId: string;
};

export const useStoredValueExpirationSchedule = (
  params: UseStoredValueExpirationScheduleParams,
) => {
  const { memberId } = params;

  const [paginationInfo, onPageChange] = usePaginationParams({
    pageSize: STORED_VALUE_EXPIRATION_PAGE_SIZE,
    beforeParamName: `${STORED_VALUE_EXPIRATION_PAGINATION_PREFIX}-before`,
    afterParamName: `${STORED_VALUE_EXPIRATION_PAGINATION_PREFIX}-after`,
  });

  const { data, loading, error, paginationProps } = usePaginatedQuery(
    useMembershipStoredValueExpirationScheduleQuery,
    'memberships.edges[0].node.storedValueExpirationSchedule.pageInfo',
    STORED_VALUE_EXPIRATION_PAGE_SIZE,
    {
      variables: {
        membershipId: memberId,
      },
    },
    paginationInfo,
    onPageChange,
  );

  const scheduleRows = useMemo(() => {
    const typedData = data as MembershipStoredValueExpirationScheduleQuery | undefined;
    const node = typedData?.memberships?.edges[0]?.node;
    if (!node) return [];

    return node.storedValueExpirationSchedule.edges?.map((e) => e.node) || [];
  }, [data]);

  return {
    loading,
    error,
    data: scheduleRows,
    paginationProps,
  };
};
